import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { IncidentRulesService as ApiIncidentRulesService } from '@api/devices';
import { Store } from '@ngrx/store';
import { MessageComponent, NotificationsService } from '@ui';
import { filter, firstValueFrom, map, take } from 'rxjs';

import { IncidentRuleCommon, NormalizedDeviceData } from '../../../models';
import { DevicesSelectors } from '../../../ngrx-store';
import { DeviceServiceV2 } from '../../../services';
import { DevicePickerComponent } from '../../device-actions/device-picker';

interface Props {
  deviceId: string;
  incidentRule: IncidentRuleCommon;
}

@Component({
  imports: [MatDialogModule, MatProgressSpinner, DevicePickerComponent, MessageComponent, MatTooltip],
  selector: 'avs-populate-rule-dialog',
  templateUrl: './populate-rule-dialog.html',
})
export class PopulateRuleDialogComponent {
  protected data = inject<Props>(MAT_DIALOG_DATA);
  protected selectedDevices = signal<NormalizedDeviceData[]>([]);
  protected isLoading = signal(false);

  protected device = toSignal(
    inject(Store).select(DevicesSelectors.getDeviceById(this.data.deviceId)).pipe(
      filter(Boolean),
      take(1),
    ),
  );

  protected devicesWithRule = toSignal(
    inject(DeviceServiceV2).list({
      filters: { withActiveIncidentRuleOnDataPoint: this.data.incidentRule.code },
    }).pipe(map(({ data }) => new Set(data.map(device => device.id)))),
    { initialValue: new Set<string>() },
  );

  protected conflictingSelected = computed(() =>
    this.selectedDevices().filter(device => this.devicesWithRule().has(device.id)),
  );

  private dialogRef = inject(MatDialogRef);
  private incidentRulesApi = inject(ApiIncidentRulesService);
  private notificationsService = inject(NotificationsService);

  protected deselectConflicting() {
    this.selectedDevices.update(devices =>
      devices.filter(device => !this.devicesWithRule().has(device.id)),
    );
  }

  protected async share() {
    if (this.isLoading() || !this.selectedDevices().length) {
      return;
    }

    this.isLoading.set(true);

    try {
      await firstValueFrom(this.incidentRulesApi.populateSimilar(
        { deviceId: this.data.deviceId, ruleId: this.data.incidentRule.id },
        {
          replaceExisting: true,
          targetDeviceIds: this.selectedDevices().map(device => device.id),
        },
      ));
      this.notificationsService.showSuccessMessage('Incident rule shared successfully');
      this.dialogRef.close(true);
    } catch (e) {
      this.notificationsService.showErrorMessage((e as HttpErrorResponse).message);
    } finally {
      this.isLoading.set(false);
    }
  }
}
